import { useState, useEffect } from 'react';
import { getGeographicAnalytics } from '../../services/geographicService';
import BarChart from '../charts/BarChart';
import PieChart from '../charts/PieChart';

const GeographicTab: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [geographicData, setGeographicData] = useState<any>(null);

  useEffect(() => {
    const loadData = async () => {
      try {
        const data = await getGeographicAnalytics();
        setGeographicData(data);
      } catch (error) {
        console.error('Error loading geographic data:', error);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, []);

  if (loading) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>Loading geographic analytics...</p>
      </div>
    );
  }

  if (!geographicData) {
    return (
      <div style={{ padding: '2rem', textAlign: 'center' }}>
        <p>No geographic data available</p>
      </div>
    );
  }

  // Language preferences
  const languageKeys = Array.from(geographicData.languagePreferences.keys());
  const languageValues = Array.from(geographicData.languagePreferences.values());
  const languageData = {
    labels: languageKeys as string[],
    datasets: [
      {
        label: 'Users',
        data: languageValues as number[],
        backgroundColor: ['#2563eb', '#10b981', '#f59e0b', '#8b5cf6', '#ec4899'],
      },
    ],
  };

  return (
    <div style={{ padding: '2rem' }}>
      <h1 style={{ marginBottom: '2rem', fontSize: '1.75rem', fontWeight: 'bold' }}>
        Geographic & Localization Insights
      </h1>

      <div style={{
        backgroundColor: '#fef3c7',
        padding: '1rem',
        borderRadius: '8px',
        marginBottom: '2rem',
        borderLeft: '4px solid #f59e0b',
      }}>
        <p style={{ margin: 0, color: '#92400e' }}>
          Note: Geographic distribution requires IP geolocation tracking. Currently showing available data.
        </p>
      </div>

      {/* Language Preferences */}
      {geographicData.languagePreferences.size > 0 && (
        <div style={{
          backgroundColor: 'white',
          padding: '1.5rem',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          marginBottom: '2rem',
        }}>
          <h3 style={{ marginTop: 0 }}>Language Preferences</h3>
          <PieChart data={languageData} height={300} />
        </div>
      )}

      {/* Language Preferences Table */}
      {geographicData.languagePreferences.size > 0 && (
        <div style={{
          backgroundColor: 'white',
          borderRadius: '8px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
          overflow: 'hidden',
        }}>
          <h3 style={{ padding: '1rem', margin: 0, borderBottom: '1px solid #e0e0e0' }}>
            Language Distribution
          </h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #e0e0e0' }}>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Language</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Users</th>
                <th style={{ padding: '1rem', textAlign: 'left' }}>Percentage</th>
              </tr>
            </thead>
            <tbody>
              {(Array.from(geographicData.languagePreferences.entries()) as [string, number][])
                .sort((a, b) => b[1] - a[1])
                .map(([language, count], index) => {
                  const total = (Array.from(geographicData.languagePreferences.values()) as number[])
                    .reduce((sum, c) => sum + c, 0);
                  const percentage = total > 0 ? (count / total) * 100 : 0;
                  return (
                    <tr key={index} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '1rem' }}>{language}</td>
                      <td style={{ padding: '1rem' }}>{count}</td>
                      <td style={{ padding: '1rem' }}>{percentage.toFixed(1)}%</td>
                    </tr>
                  );
                })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default GeographicTab;

